RivalIQ.module('graph', function (graph, App, Backbone, Marionette, $, _, d3, moment) {

    'use strict';

    const labelPosition = RivalIQ.enums.graph.position;

    /**
     * A simplifying base class that provides automatic hooks for window resize,
     * but it assumes that the SVG should be "block" level like a div (i.e., it
     * does not receive an explicit width and height as part of configuration).
     */
    graph.Block = Marionette.ItemView.mixin({
        yTicks: 5,

        ui: {
            chart: 'svg'
        },

        mixins: {
            asyncRenderWatch: Marionette.Dashboard.mixins.AsyncRenderWatch
        },

        behaviors: {
            Titles: {
                behaviorClass: RivalIQ.behaviors.Titles,
                atomic: true,
                container: 'body',
                selector: 'svg [data-toggle=tooltip]'
            },
            Resizes: {
                behaviorClass: graph.behaviors.Resizes,
                handler: 'onResize'
            }
        },

        initialize: function (options) {
            const me = this;
            me.data = me.processData(options.data);
        },

        processData: _.identity,

        template: function (data) {

            const template = _.template('<svg />');
            return template(data);
        },

        onShow: function () {

            const me = this;
            me.dimensions = me.getDimensions();
            me.renderChart();
        },

        onResize: function () {
            const me = this;
            me.isResizing = true;
            me.dimensions = me.getDimensions();
            me.renderChart();
            me.isResizing = false;
        },

        animate: function (nodes, transformFn, shouldAnimate) {
            function animationFn(transition) {
                transition
                    .duration(500)
                    .ease('sin');
            }

            if (_.exists(shouldAnimate) && !shouldAnimate) {
                return nodes.call(transformFn);
            }

            nodes
                .transition().call(animationFn)
                .call(transformFn);
        },

        refresh: function () {
            const me = this;
            // Reprocess data array
            me.data = me.processData(me.options.data);
            me.renderChart();
        },

        renderChart: function () {

            const me = this;
            const data = me.data;
            me.dimensions = me.getDimensions();
            me.startAsyncRender();

            window.requestAnimationFrame(function () {
                if (!me.isDestroyed) {
                    me.showChart(me.$('svg').get(0), data);
                }

                // NOTE: it's important to call stopAsyncRender here even if we're destroyed as it's possible that
                // a parent view is waiting on this view to render (i.e., if the this view called startAsyncRender
                // then was destroyed immediately afterward).
                me.stopAsyncRender();
            });
        },

        getYMin: _.constant(0),

        getHeight: function () {
            const me = this;
            const margin = me.getMargin();
            let height = me.getOption('height');
            const scale = _.get(me.getOption('scale'), 'y', 1);

            if (!height) {
                height = Math.max(me.$el.innerHeight(), Math.min((Math.round(me.$el.width() / 2)), 200)) * scale;
            }

            return height - margin.top - margin.bottom;
        },

        getMargin: function () {
            const me = this;
            return _.result(me, 'margin');
        },

        getDimensions: function () {
            const me = this;
            const margin = me.getMargin();

            let width = me.getOption('width');
            if (!width) {
                width = me.$el.innerWidth();
            }
            width = width - margin.left - margin.right;

            const height = me.getHeight();

            return {
                height: height,
                width: width,
                fullHeight: height + margin.bottom + margin.top,
                fullWidth: width + margin.left + margin.right
            };
        },

        showChart: function (el, data) {
            const me = this;

            let node = d3.select(el).select('.chart');

            if (node.empty()) {
                node = d3.select(el)
                    .append('g')
                    .attr('class', 'chart');

                node.append('g')
                    .attr('class', 'y grid');

                node.append('g')
                    .attr('class', 'x grid');

                node.append('g')
                    .attr('class', 'x axis');

                node.append('text')
                    .attr('class', 'x axis-label');

                node.append('g')
                    .attr('class', 'y axis');

                node.append('text')
                    .attr('class', 'y axis-label');
            }

            me.onDimensionChange(el);
            me.renderData(node, data);
            me.trigger('rendered');
        },

        onDimensionChange: function (el) {
            const me = this;
            const margin = me.getMargin();
            const dimensions = me.dimensions;

            const svg = d3.select(el)
                .attr('width', dimensions.fullWidth)
                .attr('height', dimensions.fullHeight);

            svg.select('.chart')
                .attr('transform', `translate(${margin.left},${margin.top})`);

            svg.select('.x.axis')
                .attr('transform', `translate(0,${dimensions.height})`);

            const labels = me.getOption('labels');

            if (me.hasAxisLabel('x')) {
                me._drawXAxisLabel(svg, _.result(labels, 'x'));
            }

            if (me.hasAxisLabel('y')) {
                me._drawYAxisLabel(svg, _.result(labels, 'y'));
            }
        },

        labelScale: function () {
            const me = this;

            return function (i) {
                return me.x(i);
            };
        },

        getDomain: function (metric) {
            return _.range.apply(_, metric.getDomainRange());
        },

        getMetricDataType: function (metric) {
            const me = this;

            if (!metric) {
                metric = me.getOption('concept');
            }

            if (metric instanceof RivalIQ.models.Metric) {
                return metric.getType();
            }

            return _.get(metric, 'description.dataType');
        },

        _getYTickValues: function (yMin, yMax, metric) {
            const me = this;

            let yValueScale;
            const dataType = me.getMetricDataType(metric);
            if (dataType === RivalIQ.enums.dataType.INTEGER) {
                // NOTE we have to round our max to the next tick if it is not
                // already evenly divisible by our required number of ticks
                const yRange = yMax - yMin;
                if (yRange % me.yTicks) {
                    yMax = Math.max(Math.ceil(yRange / me.yTicks), 1) * me.yTicks + yMin;
                }

                // TODO reconcile in `favorites` where negative values are
                // supported /cc @abullrd, see @crteal
                yValueScale = d3.scale.linear().domain([yMin, Math.max(yMax, me.yTicks)]);
                return yValueScale.nice().ticks(me.yTicks + 1);
            }

            yValueScale = d3.scale.linear()
                .domain([0, me.yTicks])
                .range([yMin, yMax]);

            const formattedValues = {};
            const yTickValues = _.reduce(d3.range(0, me.yTicks + 1), function (memo, i) {
                const tickValue = yValueScale(i);
                const formattedTickValue = metric.axisFormat(tickValue, {
                    tick: true
                });

                if (_.has(formattedValues, formattedTickValue)) {
                    return memo;
                }

                formattedValues[formattedTickValue] = true;

                return memo.concat(tickValue);
            }, []);

            yValueScale.domain([0, yTickValues.length - 1]);
            return _.map(_.range(0, yTickValues.length), yValueScale);
        },

        /*
         * Build yaxis function
         * Max ticks specified by yTicks, may pick less if the formatter
         * results in duplicate tick values
         */
        _yAxis: function (metric) {
            const me = this;

            const domain = me.y.domain();

            let yMin = _.head(domain);
            if (_.isNaN(yMin)) {
                yMin = 0;
            }

            let yMax = _.last(domain);
            if (_.isNaN(yMax)) {
                yMax = 1;
            }

            return function () {
                return d3.svg.axis()
                    .scale(me.y)
                    .orient('left')
                    .tickValues(me._getYTickValues(yMin, yMax, metric))
                    .tickFormat(function (d) {
                        return metric.axisFormat(d, {
                            tick: true
                        });
                    });
            };
        },

        _drawTicks: function (svg, data, metric) {
            const me = this;

            metric = (metric || me.getOption('concept'));

            const tickValues = metric.getTicks(me.getOption('ticks'), me.dimensions.width);

            const domain = me.getDomain(metric);
            const xLabelScale = d3.scale.ordinal()
                .range(_.map(domain, _.bind(me.labelScale(domain), me)))
                .domain(domain);

            const continuous = metric.hasContinuousDomain();
            const xAxis = d3.svg.axis()
                .scale(xLabelScale)
                .tickValues(tickValues)
                .tickFormat(function (d) {
                    const tickRange = metric.getTick(d, {
                        format: false
                    });
                    let tick = tickRange[0];

                    // NOTE We place interior labels in the middle of their
                    // range. For continuous domains we should therefore use
                    // labels from the middle of that range
                    if (continuous) {
                        if (d === _.last(domain)) {
                            tick = tickRange[1];
                        } else if (d !== 0) {
                            tick = moment.utc((tickRange[0].toDate().getTime() + tickRange[1].toDate().getTime()) / 2);
                        }
                    }

                    return metric.domainFormat(tick);
                })
                .orient('bottom');

            const createYAxis = me._yAxis(metric);

            svg.select('.grid')
                .call(createYAxis().tickSize(-me.dimensions.width, 0, 0).tickFormat(''));

            svg.select('.x.axis')
                .attr('transform', `translate(0,${me.dimensions.height})`)
                .call(xAxis);

            // NOTE phantom breaks if we use an svg element
            const bounds = me.el.getBoundingClientRect();
            svg.selectAll('g.x.axis .tick')
                .each(_.partial(me.setTextAnchor, me, domain, bounds));

            svg.select('.y.axis')
                .call(createYAxis());
        },

        getTickPosition: function (domain, i) {

            const me = this;

            const tickPosition = me.getOption('tickPosition');
            if (!_.isUndefined(tickPosition)) {
                return tickPosition;
            }

            if (domain.length === 1) {
                return labelPosition.MIDDLE;
            }

            if (i === domain[0]) {
                return labelPosition.LEFT;
            }

            if (i === domain[domain.length - 1]) {
                return labelPosition.RIGHT;
            }

            return labelPosition.MIDDLE;
        },

        setTextAnchor: function (graph, domain, bounds, d) {
            const text = d3.select(this).select('text');

            switch (graph.getTickPosition(domain, d)) {
                case labelPosition.LEFT:
                    return text.style('text-anchor', 'start');
                case labelPosition.RIGHT:
                    return text.style('text-anchor', 'end');
                default:
                    break;
            }

            // Adjust label placement to prevent overflowing the svg
            const labelEdge = this.getBoundingClientRect().right;
            if (labelEdge > bounds.right) {
                text.attr('dx', (bounds.right - labelEdge));
            }
        },

        _drawXAxisLabel: function (svg, label) {
            const me = this;
            const dimensions = me.dimensions;
            const x = (dimensions.width / 2);
            const y = (dimensions.fullHeight - (me.margin.bottom * 0.8));
            svg.select('.x.axis-label')
                .attr('text-anchor', 'middle')
                .attr('transform', `translate(${x},${y})`)
                .text(label);
        },

        _drawYAxisLabel: function (svg, label) {
            const me = this;
            svg.select('.y.axis-label')
                .attr('transform', 'rotate(-90)')
                .attr('y', 0 - me.getLeftMargin())
                .attr('x', 0 - (me.dimensions.height / 2))
                .attr('dy', '1em')
                .attr('text-anchor', 'middle')
                .text(label);
        },

        getLeftMargin: function () {
            const me = this;
            return _.get(me.getMargin(), 'left', 0);
        },

        hasAxisLabel: function (axis) {
            const me = this;
            const labels = me.getOption('labels');
            return (_.has(labels, axis) && _.result(labels, axis));
        }
    });

}, window.d3, window.moment);
